import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, LifeBuoy } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { HELP_CATEGORIES, SITE } from "@/data/site";

export default function HelpCenter() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const categories = HELP_CATEGORIES.map((c) => ({
    ...c,
    items: q
      ? c.items.filter((i) => i.toLowerCase().includes(q) || c.title.toLowerCase().includes(q))
      : c.items,
  })).filter((c) => c.items.length > 0);

  return (
    <>
      <Seo
        title="Help Center"
        description="Find answers, guides and support for Kinect Nova AI — getting started, emergency features, medications, teleconsultation, billing and privacy."
        path="/help"
      />
      <PageHeader
        eyebrow="Help Center"
        title="How can we help?"
        subtitle="Browse our guides or search for an answer. Still stuck? Our team is one email away."
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <Reveal className="mx-auto max-w-2xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="help-search-input"
              placeholder="Search help articles..."
              className="h-14 rounded-2xl pl-12 text-base"
            />
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal
                key={c.title}
                delay={i * 0.05}
                className="rounded-3xl border border-border bg-card p-7"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-foreground">{c.title}</h3>
                <ul className="mt-4 space-y-1">
                  {c.items.map((item) => (
                    <li key={item}>
                      <Link
                        to="/contact"
                        className="group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        {item}
                        <ChevronRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>

        {categories.length === 0 && (
          <p className="mt-12 text-center text-muted-foreground" data-testid="help-no-results">
            No articles found for "{query}". Try a different search or{" "}
            <Link to="/contact" className="font-medium text-primary hover:underline">contact us</Link>.
          </p>
        )}

        <Reveal className="mt-16 flex flex-col items-center justify-between gap-6 rounded-[2rem] border border-border bg-secondary/40 p-10 text-center sm:flex-row sm:text-left">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <LifeBuoy className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground">Still need help?</h3>
              <p className="text-sm text-muted-foreground">Email us at {SITE.supportEmail} — we're happy to help.</p>
            </div>
          </div>
          <Link
            to="/contact"
            data-testid="help-contact-btn"
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
          >
            Contact support
          </Link>
        </Reveal>
      </section>
    </>
  );
}
